/**
 * Companies View
 *
 * Admin-only page for managing companies (organizations).
 */

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { type ColumnDef } from "@tanstack/react-table";
import { Card } from "@/ui/atoms/Card";
import { Loader, Modal, DialogFooter } from "@/ui/atoms";
import { DataTable } from "@/ui/molecules";
import {
    Building2,
    Plus,
    Edit2,
    Flag,
    Trash2,
    Check,
    X,
    Mail,
    Globe,
    Image,
    Upload,
    XCircle,
} from "lucide-react";
import type { TAsyncState } from "@/store";
import type { TApiCompanyFeatureFlags } from "@/apis/proda_finance/types";
import { FEATURE_FLAG_DEFINITIONS } from "@/utils/featureFlagDefinitions";
import { EMPTY_ARRAY } from "@/utils/utils";
import { formatExcelsiorId, parseExcelsiorId } from "@/utils/excelsiorId";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { booleanParam, sortParam, textParam } from "@/useCases/serverTableQuery";

/**
 * Company type for the view
 */
export type TCompany = {
    id: string;
    name: string;
    slug: string;
    code: string;
    emailDomains: string[];
    contactEmail: string | null;
    contactName: string | null;
    excelsiorCompanyId: number | null;
    isActive: boolean;
    hasLogo: boolean;
    logoUrl: string | null;
    featureFlags: TApiCompanyFeatureFlags;
    /** Customer module slug; null means the company gets platform features only. */
    customModule: string | null;
    createdAt: string;
    updatedAt: string;
};

export type TCustomerModuleOption = {
    slug: string;
    displayName: string;
};

/** Select value for "no customer module" (a select cannot hold null). */
const NO_CUSTOMER_MODULE = "";

type TCompaniesQuery = {
    skip?: number;
    limit?: number;
    name?: string;
    code?: string;
    contact?: string;
    email_domains?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: string;
};

export type TViewCompaniesParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useCompaniesState: () => TAsyncState<TCompany[]>;
        usePagination: () => { page: number; pageSize: number; total: number };
        onPageChange: (page: number) => void;
        loadCompanies: (query: TCompaniesQuery, signal?: AbortSignal) => void;
        useCompaniesTotalUnfiltered: () => number;
        onCreateCompany: (data: {
            name: string;
            slug: string;
            code: string;
            emailDomains?: string[];
            contactEmail?: string | null;
            contactName?: string | null;
            excelsiorCompanyId?: number | null;
        }) => Promise<string>; // Returns the created company ID
        onUpdateCompany: (
            id: string,
            data: {
                name?: string;
                slug?: string;
                code?: string;
                emailDomains?: string[];
                contactEmail?: string | null;
                contactName?: string | null;
                excelsiorCompanyId?: number | null;
                isActive?: boolean;
            },
        ) => Promise<void>;
        onUpdateCompanyConfig: (
            id: string,
            data: {
                emailCommunicationsEnabled: boolean;
                homepageV2Enabled: boolean;
                operatingStatementEnabled: boolean;
                comparisonEnabled: boolean;
                customModule: string | null;
            },
        ) => Promise<void>;
        loadCustomerModules: () => Promise<TCustomerModuleOption[]>;
        onDeleteCompany: (id: string) => Promise<void>;
        onUploadLogo: (id: string, logo: File) => Promise<void>;
        onDeleteLogo: (id: string) => Promise<void>;
        onRefresh: () => void;
    };
};

export type TViewCompaniesProps = Record<string, never>;

// Protected company slugs whose identity cannot be edited, deactivated or
// deleted. Feature flags (the Config modal) stay editable for them: they are
// rollout switches, and Proda must be able to flip its own.
const PROTECTED_COMPANY_SLUGS = new Set(["proda"]);

const isProtectedCompany = (company: TCompany): boolean => {
    return PROTECTED_COMPANY_SLUGS.has(company.slug);
};

// Allowed logo file types
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
const MAX_LOGO_SIZE_MB = 2;
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;

const PAGE_SIZE = 100;

export const createViewCompanies = ({ useCases }: TViewCompaniesParams) => {
    return function ViewCompanies() {
        const {
            onMount,
            onUnmount,
            useCompaniesState,
            usePagination,
            loadCompanies,
            useCompaniesTotalUnfiltered,
            onCreateCompany,
            onUpdateCompany,
            onUpdateCompanyConfig,
            loadCustomerModules,
            onDeleteCompany,
            onUploadLogo,
            onDeleteLogo,
            onRefresh,
        } = useCases();

        const pagination = usePagination();
        const companiesTotalUnfiltered = useCompaniesTotalUnfiltered();

        const companiesState = useCompaniesState();

        const companiesToQuery = useCallback(
            (input: TServerTableQueryInput) => ({
                skip: (input.page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
                name: textParam(input.columnFilters, "name"),
                code: textParam(input.columnFilters, "code"),
                contact: textParam(input.columnFilters, "contactName"),
                email_domains: textParam(input.columnFilters, "emailDomains"),
                is_active: booleanParam(input.columnFilters, "isActive"),
                ...sortParam(input.sorting),
            }),
            [],
        );

        const server = useServerTable({
            pageSize: PAGE_SIZE,
            toQuery: companiesToQuery,
            fetchData: loadCompanies,
        });
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [editingCompany, setEditingCompany] = useState<TCompany | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Config modal state (per-company feature flags)
        const [configCompany, setConfigCompany] = useState<TCompany | null>(null);
        const [configFlags, setConfigFlags] = useState<Record<string, boolean>>({});
        const [configCustomModule, setConfigCustomModule] = useState<string>(NO_CUSTOMER_MODULE);
        const [customerModules, setCustomerModules] = useState<TCustomerModuleOption[]>([]);
        const [customerModulesError, setCustomerModulesError] = useState<string | null>(null);
        const [isSavingConfig, setIsSavingConfig] = useState(false);
        const [configError, setConfigError] = useState<string | null>(null);

        // Logo state (integrated into main modal)
        const [logoFile, setLogoFile] = useState<File | null>(null);
        const [logoPreview, setLogoPreview] = useState<string | null>(null);
        const [logoError, setLogoError] = useState<string | null>(null);
        const [shouldRemoveLogo, setShouldRemoveLogo] = useState(false);
        const logoInputRef = useRef<HTMLInputElement>(null);

        // Form state (emailDomains stored as comma-separated string for easy editing)
        const [formData, setFormData] = useState({
            name: "",
            slug: "",
            code: "",
            emailDomains: "",
            contactEmail: "",
            contactName: "",
            excelsiorCompanyId: "",
        });

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const resetForm = () => {
            setFormData({
                name: "",
                slug: "",
                code: "",
                emailDomains: "",
                contactEmail: "",
                contactName: "",
                excelsiorCompanyId: "",
            });
            setLogoFile(null);
            setLogoPreview(null);
            setLogoError(null);
            setShouldRemoveLogo(false);
        };

        const openCreateModal = () => {
            resetForm();
            setEditingCompany(null);
            setIsModalOpen(true);
        };

        const openEditModal = useCallback((company: TCompany) => {
            setFormData({
                name: company.name,
                slug: company.slug,
                code: company.code,
                emailDomains: company.emailDomains.join(", "),
                contactEmail: company.contactEmail || "",
                contactName: company.contactName || "",
                excelsiorCompanyId: formatExcelsiorId(company.excelsiorCompanyId),
            });
            setEditingCompany(company);
            setLogoFile(null);
            setLogoPreview(company.logoUrl);
            setLogoError(null);
            setShouldRemoveLogo(false);
            setIsModalOpen(true);
        }, []);

        const closeModal = () => {
            setIsModalOpen(false);
            setEditingCompany(null);
            resetForm();
        };

        // Logo handlers
        const handleLogoFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            // Validate file type
            if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
                setLogoError("Invalid file type. Please upload PNG, JPG, or SVG.");
                return;
            }

            // Validate file size
            if (file.size > MAX_LOGO_SIZE_BYTES) {
                setLogoError(`File too large. Maximum size is ${MAX_LOGO_SIZE_MB}MB.`);
                return;
            }

            setLogoError(null);
            setLogoFile(file);
            setShouldRemoveLogo(false);

            // Generate preview using browser's FileReader API
            const reader = new window.FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }, []);

        const handleRemoveLogo = useCallback(() => {
            setLogoFile(null);
            setLogoPreview(null);
            setShouldRemoveLogo(true);
            if (logoInputRef.current) {
                logoInputRef.current.value = "";
            }
        }, []);

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmitting(true);
            try {
                // Parse comma-separated domains into array
                const domains = formData.emailDomains
                    .split(",")
                    .map((d) => d.trim().toLowerCase())
                    .filter((d) => d.length > 0);

                const excelsiorCompanyId = parseExcelsiorId(formData.excelsiorCompanyId);
                if (excelsiorCompanyId === undefined) {
                    console.error("Excelsior company id must be a positive whole number");
                    return;
                }

                if (editingCompany) {
                    // Update existing company
                    await onUpdateCompany(editingCompany.id, {
                        name: formData.name,
                        slug: formData.slug,
                        code: formData.code.toUpperCase(),
                        emailDomains: domains,
                        contactEmail: formData.contactEmail || null,
                        contactName: formData.contactName || null,
                        excelsiorCompanyId,
                    });

                    // Handle logo changes
                    if (shouldRemoveLogo && editingCompany.hasLogo) {
                        await onDeleteLogo(editingCompany.id);
                    } else if (logoFile) {
                        await onUploadLogo(editingCompany.id, logoFile);
                    }
                } else {
                    // Create new company and get the ID
                    const newCompanyId = await onCreateCompany({
                        name: formData.name,
                        slug: formData.slug,
                        code: formData.code.toUpperCase(),
                        emailDomains: domains,
                        contactEmail: formData.contactEmail || null,
                        contactName: formData.contactName || null,
                        excelsiorCompanyId,
                    });

                    // If logo was provided, upload it for the new company
                    if (logoFile && newCompanyId) {
                        await onUploadLogo(newCompanyId, logoFile);
                    }
                }
                closeModal();
            } catch (error) {
                console.error("Failed to save company:", error);
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleToggleActive = useCallback(
            async (company: TCompany) => {
                try {
                    await onUpdateCompany(company.id, { isActive: !company.isActive });
                } catch (error) {
                    console.error("Failed to toggle company status:", error);
                }
            },
            [onUpdateCompany],
        );

        const openConfigModal = useCallback((company: TCompany) => {
            setConfigCompany(company);
            const flags = (company.featureFlags ?? {}) as Record<string, boolean | undefined>;
            setConfigFlags(
                Object.fromEntries(
                    FEATURE_FLAG_DEFINITIONS.map(({ key }) => [key, flags[key] ?? false]),
                ),
            );
            setConfigCustomModule(company.customModule ?? NO_CUSTOMER_MODULE);
            setConfigError(null);
        }, []);

        // The dropdown options come from the backend registry, so a new
        // customer module appears here without a frontend change.
        useEffect(() => {
            if (!configCompany) return;
            let cancelled = false;
            setCustomerModulesError(null);
            loadCustomerModules()
                .then((modules) => {
                    if (!cancelled) setCustomerModules(modules);
                })
                .catch((error: unknown) => {
                    if (!cancelled) {
                        setCustomerModulesError(
                            error instanceof Error
                                ? error.message
                                : "Failed to load customer modules",
                        );
                    }
                });
            return () => {
                cancelled = true;
            };
        }, [configCompany, loadCustomerModules]);

        const closeConfigModal = useCallback(() => {
            setConfigCompany(null);
            setConfigError(null);
        }, []);

        const handleSaveConfig = useCallback(
            async (e: React.FormEvent) => {
                e.preventDefault();
                if (!configCompany) return;
                setIsSavingConfig(true);
                setConfigError(null);
                try {
                    await onUpdateCompanyConfig(configCompany.id, {
                        emailCommunicationsEnabled:
                            configFlags["email_communications_enabled"] ?? false,
                        homepageV2Enabled: configFlags["homepage_v2_enabled"] ?? false,
                        operatingStatementEnabled:
                            configFlags["operating_statement_enabled"] ?? false,
                        comparisonEnabled: configFlags["comparison_enabled"] ?? false,
                        customModule:
                            configCustomModule === NO_CUSTOMER_MODULE ? null : configCustomModule,
                    });
                    setConfigCompany(null);
                } catch (error) {
                    setConfigError(
                        error instanceof Error ? error.message : "Failed to save configuration",
                    );
                } finally {
                    setIsSavingConfig(false);
                }
            },
            [configCompany, configFlags, configCustomModule, onUpdateCompanyConfig],
        );

        const handleDelete = useCallback(
            async (company: TCompany) => {
                if (
                    !confirm(
                        `Are you sure you want to delete "${company.name}"? This action cannot be undone.`,
                    )
                ) {
                    return;
                }
                try {
                    await onDeleteCompany(company.id);
                } catch (error) {
                    console.error("Failed to delete company:", error);
                }
            },
            [onDeleteCompany],
        );

        // Define table columns
        const columns: ColumnDef<TCompany>[] = useMemo(
            () => [
                {
                    accessorKey: "logo",
                    header: "Logo",
                    cell: ({ row }) => (
                        <div className="flex items-center justify-center w-12 h-12">
                            {row.original.hasLogo && row.original.logoUrl ? (
                                <img
                                    src={row.original.logoUrl}
                                    alt={`${row.original.name} logo`}
                                    className="max-w-full max-h-full object-contain rounded"
                                />
                            ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                            )}
                        </div>
                    ),
                    enableSorting: false,
                },
                {
                    accessorKey: "name",
                    header: "Company",
                    cell: ({ row }) => (
                        <div>
                            <p className="font-medium">{row.original.name}</p>
                            <p className="text-xs text-muted-foreground">{row.original.slug}</p>
                        </div>
                    ),
                    meta: {
                        filterType: "text",
                        filterPlaceholder: "Search company...",
                    },
                    filterFn: (row, _columnId, filterValue) => {
                        if (!filterValue) return true;
                        const searchTerm = (filterValue as string).toLowerCase();
                        return (
                            row.original.name.toLowerCase().includes(searchTerm) ||
                            row.original.slug.toLowerCase().includes(searchTerm)
                        );
                    },
                },
                {
                    accessorKey: "code",
                    header: "Code",
                    cell: ({ row }) => (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-xs font-mono">
                            {row.original.code}
                        </span>
                    ),
                    meta: {
                        filterType: "text",
                        filterPlaceholder: "Search code...",
                    },
                },
                {
                    accessorKey: "customModule",
                    header: "Customer module",
                    enableSorting: false,
                    enableColumnFilter: false,
                    cell: ({ row }) =>
                        row.original.customModule ? (
                            <span
                                className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-xs font-mono"
                                data-testid={`company-customer-module-${row.original.slug}`}
                            >
                                {row.original.customModule}
                            </span>
                        ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                        ),
                },
                {
                    accessorKey: "emailDomains",
                    header: "Email Domains",
                    cell: ({ row }) =>
                        row.original.emailDomains.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {row.original.emailDomains.map((domain) => (
                                    <span
                                        key={domain}
                                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs"
                                    >
                                        @{domain}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                        ),
                    meta: {
                        filterType: "text",
                        filterPlaceholder: "Search domain...",
                    },
                    filterFn: (row, _columnId, filterValue) => {
                        if (!filterValue) return true;
                        const searchTerm = (filterValue as string).toLowerCase();
                        return row.original.emailDomains.some((d) =>
                            d.toLowerCase().includes(searchTerm),
                        );
                    },
                    enableSorting: false,
                },
                {
                    accessorKey: "contactName",
                    header: "Contact",
                    cell: ({ row }) =>
                        row.original.contactEmail || row.original.contactName ? (
                            <div className="text-sm">
                                {row.original.contactName && (
                                    <p className="font-medium">{row.original.contactName}</p>
                                )}
                                {row.original.contactEmail && (
                                    <p className="text-muted-foreground text-xs">
                                        {row.original.contactEmail}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                        ),
                    meta: {
                        filterType: "text",
                        filterPlaceholder: "Search contact...",
                    },
                    filterFn: (row, _columnId, filterValue) => {
                        if (!filterValue) return true;
                        const searchTerm = (filterValue as string).toLowerCase();
                        return (
                            (row.original.contactName?.toLowerCase().includes(searchTerm) ??
                                false) ||
                            (row.original.contactEmail?.toLowerCase().includes(searchTerm) ?? false)
                        );
                    },
                },
                {
                    accessorKey: "isActive",
                    header: "Status",
                    cell: ({ row }) => {
                        const company = row.original;
                        return (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isProtectedCompany(company)) handleToggleActive(company);
                                }}
                                disabled={isProtectedCompany(company)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                    company.isActive
                                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                } ${isProtectedCompany(company) ? "cursor-not-allowed opacity-70" : ""}`}
                                title={
                                    isProtectedCompany(company) ? "Protected company" : undefined
                                }
                            >
                                {company.isActive ? (
                                    <>
                                        <Check className="h-3 w-3" />
                                        Active
                                    </>
                                ) : (
                                    <>
                                        <X className="h-3 w-3" />
                                        Inactive
                                    </>
                                )}
                            </button>
                        );
                    },
                    meta: {
                        filterType: "boolean",
                        filterPlaceholder: "All statuses",
                    },
                    filterFn: (row, _columnId, filterValue) => {
                        if (filterValue === undefined) return true;
                        return row.original.isActive === filterValue;
                    },
                },
                {
                    id: "actions",
                    header: "",
                    cell: ({ row }) => {
                        const company = row.original;
                        return (
                            <div className="flex items-center justify-end gap-1">
                                {isProtectedCompany(company) ? (
                                    <span
                                        className="px-2 py-1 text-xs text-muted-foreground bg-muted rounded-md"
                                        title="This company is protected and cannot be modified"
                                    >
                                        Protected
                                    </span>
                                ) : (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditModal(company);
                                        }}
                                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                        title="Edit"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openConfigModal(company);
                                    }}
                                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                    title="Config"
                                    data-testid={`company-config-${company.slug}`}
                                >
                                    <Flag className="h-4 w-4" />
                                </button>
                                {!isProtectedCompany(company) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(company);
                                        }}
                                        className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        );
                    },
                    enableSorting: false,
                },
            ],
            [handleToggleActive, handleDelete, openEditModal, openConfigModal],
        );

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Building2 className="h-6 w-6" />
                            Company Management
                        </h1>
                        <p className="text-muted-foreground">
                            Manage organizations and their email domain mappings
                        </p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add Company
                    </button>
                </div>

                {/* Create/Edit Modal */}
                <Modal
                    open={isModalOpen}
                    onOpenChange={(open) => {
                        if (!open) closeModal();
                    }}
                    title={editingCompany ? "Edit Company" : "Create New Company"}
                    description={
                        editingCompany
                            ? "Update the company's information below."
                            : "Fill in the details to create a new organization."
                    }
                >
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium mb-1">
                                    Name <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder="Organization Name"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Slug <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            slug: e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, "-"),
                                        })
                                    }
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder="organization-slug"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Code <span className="text-destructive">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            code: e.target.value
                                                .toUpperCase()
                                                .replace(/[^A-Z0-9]/g, ""),
                                        })
                                    }
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary uppercase"
                                    placeholder="ORG"
                                    maxLength={10}
                                    required
                                />
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium mb-1">
                                        <Globe className="inline h-4 w-4 mr-1" />
                                        Email Domains
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.emailDomains}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                emailDomains: e.target.value
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9.,-\s]/g, ""),
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="company.com, company.co.uk"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Comma-separated list of domains. Users with these domains
                                        will auto-join this company.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        <Mail className="inline h-4 w-4 mr-1" />
                                        Contact Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.contactEmail}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                contactEmail: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="admin@company.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Contact Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.contactName}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                contactName: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="company-excelsior-id"
                                        className="block text-sm font-medium mb-1"
                                    >
                                        Excelsior Company ID
                                    </label>
                                    <input
                                        id="company-excelsior-id"
                                        data-testid="company-excelsior-id-input"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={formData.excelsiorCompanyId}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                excelsiorCompanyId: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="e.g. 9072"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Links this company to Excelsior for permissions. Leave blank
                                        if not linked.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Logo Section */}
                        <div className="border-t pt-4">
                            <label className="block text-sm font-medium mb-2">
                                <Image className="inline h-4 w-4 mr-1" />
                                Logo (optional)
                            </label>
                            <div className="flex items-start gap-4">
                                {/* Logo Preview */}
                                <div className="relative w-20 h-20 border-2 border-dashed border-muted rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden flex-shrink-0">
                                    {logoPreview && !shouldRemoveLogo ? (
                                        <img
                                            src={logoPreview}
                                            alt="Logo preview"
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    ) : (
                                        <div className="text-center text-muted-foreground">
                                            <Image className="h-6 w-6 mx-auto opacity-50" />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 space-y-2">
                                    {/* File Input (hidden) */}
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                        onChange={handleLogoFileChange}
                                        className="hidden"
                                    />

                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => logoInputRef.current?.click()}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-muted transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            <Upload className="h-3.5 w-3.5" />
                                            {logoPreview && !shouldRemoveLogo
                                                ? "Replace"
                                                : "Upload"}
                                        </button>

                                        {((editingCompany?.hasLogo && !shouldRemoveLogo) ||
                                            logoFile) && (
                                            <button
                                                type="button"
                                                onClick={handleRemoveLogo}
                                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-destructive border border-destructive/30 rounded-lg hover:bg-destructive/10 transition-colors"
                                                disabled={isSubmitting}
                                            >
                                                <XCircle className="h-3.5 w-3.5" />
                                                Remove
                                            </button>
                                        )}
                                    </div>

                                    {/* File info */}
                                    {logoFile && (
                                        <p className="text-xs text-muted-foreground">
                                            {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                                        </p>
                                    )}

                                    {/* Error message */}
                                    {logoError && (
                                        <p className="text-xs text-destructive">{logoError}</p>
                                    )}

                                    {/* File requirements */}
                                    <p className="text-xs text-muted-foreground">
                                        PNG, JPG, or SVG • Max {MAX_LOGO_SIZE_MB}MB
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 pt-4">
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader size="sm" className="inline mr-2" />
                                        Saving...
                                    </>
                                ) : editingCompany ? (
                                    "Save Changes"
                                ) : (
                                    "Create Company"
                                )}
                            </button>
                        </DialogFooter>
                    </form>
                </Modal>

                {/* Config modal — per-company feature flags (Proda admins only) */}
                {configCompany && (
                    <Modal
                        open={true}
                        onOpenChange={(open) => {
                            if (!open) closeConfigModal();
                        }}
                        title={`Configure ${configCompany.name}`}
                        description="Choose the company's customer module and optional features."
                    >
                        <form onSubmit={handleSaveConfig} className="space-y-4">
                            <div className="space-y-1">
                                <label
                                    htmlFor="config-customer-module"
                                    className="text-sm font-medium"
                                >
                                    Customer module
                                </label>
                                <select
                                    id="config-customer-module"
                                    className="w-full rounded-lg border px-3 py-2 text-sm bg-background"
                                    value={configCustomModule}
                                    onChange={(e) => setConfigCustomModule(e.target.value)}
                                    disabled={isSavingConfig}
                                    data-testid="config-customer-module"
                                >
                                    <option value={NO_CUSTOMER_MODULE}>
                                        None (platform features only)
                                    </option>
                                    {customerModules.map((m) => (
                                        <option key={m.slug} value={m.slug}>
                                            {m.displayName}
                                        </option>
                                    ))}
                                    {/* Keep a stored value selectable even if the list
                                        has not loaded or no longer contains it. */}
                                    {configCustomModule !== NO_CUSTOMER_MODULE &&
                                        !customerModules.some(
                                            (m) => m.slug === configCustomModule,
                                        ) && (
                                            <option value={configCustomModule}>
                                                {configCustomModule}
                                            </option>
                                        )}
                                </select>
                                <span className="block text-xs text-muted-foreground">
                                    Decides which PRODA Finance product this company&apos;s users
                                    get: its pages, workflow and exports. Users, support,
                                    announcements and audit log are always available.
                                </span>
                                {customerModulesError && (
                                    <p className="text-xs text-destructive">
                                        {customerModulesError}
                                    </p>
                                )}
                            </div>

                            {FEATURE_FLAG_DEFINITIONS.map(({ key, label, description }) => (
                                <label key={key} className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={configFlags[key] ?? false}
                                        onChange={(e) =>
                                            setConfigFlags((prev) => ({
                                                ...prev,
                                                [key]: e.target.checked,
                                            }))
                                        }
                                        disabled={isSavingConfig}
                                        data-testid={`config-${key.replace(/_enabled$/, "").replace(/_/g, "-")}`}
                                    />
                                    <span>
                                        <span className="text-sm font-medium">{label}</span>
                                        <span className="block text-xs text-muted-foreground">
                                            {description}
                                        </span>
                                    </span>
                                </label>
                            ))}

                            {configError && (
                                <p className="text-sm text-destructive">{configError}</p>
                            )}

                            <DialogFooter className="gap-2 pt-4">
                                <button
                                    type="button"
                                    onClick={closeConfigModal}
                                    className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors"
                                    disabled={isSavingConfig}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                                    disabled={isSavingConfig}
                                    data-testid="config-save"
                                >
                                    {isSavingConfig ? (
                                        <>
                                            <Loader size="sm" className="inline mr-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Save Configuration"
                                    )}
                                </button>
                            </DialogFooter>
                        </form>
                    </Modal>
                )}

                {/* Companies list */}
                <Card className="overflow-hidden">
                    {companiesState.status === "rejected" ? (
                        <div className="p-6 text-center text-destructive">
                            <p>Failed to load companies</p>
                            <button
                                onClick={onRefresh}
                                className="mt-2 text-sm underline hover:no-underline"
                            >
                                Try again
                            </button>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            storageKey="companies"
                            data={companiesState.data ?? EMPTY_ARRAY}
                            loading={companiesState.status === "pending"}
                            emptyMessage="No companies found."
                            manualFiltering
                            manualSorting
                            sortingState={server.sorting}
                            onSortingChange={server.onSortingChange}
                            initialColumnFilters={server.columnFilters}
                            onColumnFiltersChange={server.onColumnFiltersChange}
                            rowNoun={{ one: "company", many: "companies" }}
                            rowCount={buildRowCount({
                                total: pagination.total,
                                totalUnfiltered: companiesTotalUnfiltered,
                                hasActiveFilters: server.columnFilters.length > 0,
                            })}
                            pagination={{
                                page: server.page,
                                pageSize: PAGE_SIZE,
                                total: pagination.total,
                                onPageChange: server.setPage,
                            }}
                        />
                    )}
                </Card>
            </div>
        );
    };
};
